import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    StyleSheet,
    View,
    SafeAreaView,
    useWindowDimensions,
    FlatList,
    ActivityIndicator,
} from "react-native";
import { Card, Checkbox } from "react-native-paper";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../../styles/commonStyles";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomButton from "../../../components/CustomButton";
import CustomDropDown from "../../../components/CustomDropDown/CustomDropdown";
import { useTranslation } from "react-i18next";
import { useHRAdminEmployees } from "../../../hooks/useHRAdminEmployees";
import { useCompany } from "../../../hooks/useCompany";
import { useTableHelper } from "../../../hooks/useTableHelper";
import Swal from "sweetalert2";
import { showMessage } from "react-native-flash-message";

const ReportsToListView = React.memo(
    ({
        item,
        index,
        handleEditPress,
        styles,
        reportsToHelper,
        t,
        handleDelete,
    }) => {
        return (
            <View>
                <SafeAreaView>
                    <SafeAreaView style={styles.row}>
                        <View style={[styles.item]}>
                            <CustomDropDown
                                defValue={item.managerID}
                                options={reportsToHelper.Manager}
                                setValue={(e) =>
                                    handleEditPress(index, item, "managerID", e)
                                }
                                type="REPORTS_TO"
                            />
                        </View>
                        <View style={[styles.item2]}>
                            <Checkbox
                                status={item.notifieduponapprovalonly ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, "notifieduponapprovalonly", !item.notifieduponapprovalonly)}
                                color={item.notifieduponapprovalonly ? '#86BC25' : undefined}
                                style={styles.checkbox}
                            />
                        </View>
                        <View style={[styles.item2]}>
                            <Checkbox
                                status={item.isprimary ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, "isprimary", !item.isprimary)}
                                color={item.isprimary ? '#86BC25' : undefined}
                                style={styles.checkbox}
                            />
                        </View>
                        <View style={[styles.item2]}>
                            <Checkbox
                                status={item.approvesLeaves ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, "approvesLeaves", !item.approvesLeaves)}
                                color={item.approvesLeaves ? '#86BC25' : undefined}
                                style={styles.checkbox}
                            />
                        </View>
                        <View style={[styles.item2]}>
                            <Checkbox
                                status={item.approvesTimesheets ? 'checked' : 'unchecked'}
                                onPress={() => handleEditPress(index, item, "approvesTimesheets", !item.approvesTimesheets)}
                                color={item.approvesTimesheets ? '#86BC25' : undefined}
                                style={styles.checkbox}
                            />
                        </View>
                        <View style={[styles.item2, { alignItems: 'flex-end' }]}>
                            <CustomButton
                                iconName="trash-outline"
                                onPress={() => handleDelete(item, index, 'rowguid')}
                                show="HOME_BELL"
                                type="HOME_BELL"
                                textTop="2"
                                sz={30}
                            />
                        </View>
                    </SafeAreaView>
                </SafeAreaView>
            </View>
        );
    }
);

const HRAdminEmplReportsTo = (props) => {
    const { width } = useWindowDimensions();
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width);
    const [changes, setChanges] = useState([]);
    const [deletedItems, setDeletedItems] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    // * Will not investigate duplicates for this due to time. Re-enable and check salary schemes to see how to implement. 
    // const [duplicates, setDuplicates] = useState([]);
    const [reportsToHelper, setReportsToHelper] = useState({});
    const [reportsTo, setReportsTo] = useState([]);
    const { t } = useTranslation();
    const ref = useRef(null);
    const { getReportsToHelper, getReportsTo, saveReportsTo, deleteReportsTo, isLoading } = useHRAdminEmployees();
    const { getAllEmployees } = useCompany();
    const { rowAdd, rowEdit, rowDelete } = useTableHelper();

    const showWarningPopup = async () => {
        return Swal.fire({
            title: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
                "common:changeSchemeMessage"
            )}</text>`,
            icon: "warning",
            showCancelButton: true,
            reverseButtons: true,
            cancelButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
                "common:cancel"
            )}</text>`,
            cancelButtonColor: "#d3d3d3",
            confirmButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
                "common:confirmMessage"
            )}</text>`,
            confirmButtonColor: "#A0A2FF",
        });
    };

    useEffect(() => {
        loadData();
    }, [props?.selectedEmployee, props?.selectedCompany]);

    const loadData = () => {
        getReportsToHelper(props?.selectedCompany?.code).then((reportsToHelperResp) => {
            if(!reportsToHelperResp){
                showMessage({
                    message: t('common:loadManagersFailed'),
                    type: "error",
                    duration: 3000,
                })
            }
            setReportsToHelper(reportsToHelperResp);
            getReportsTo(props?.selectedEmployee?.employeeID).then((reportsToResp) => {
                setReportsTo(reportsToResp);
                setOriginalData(reportsToResp)
            }).catch((error) => console.error(error))
        }).catch((error) => console.error(error))
    }

    const handleEditPress = useCallback(
        async (index, item, key, value) => {
            // Check if the value has changed
            if (item[key] === value) return;
            // Store the original value for potential rollback
            // Use rowEdit to apply the change
            rowEdit(
                index,
                item,
                key,
                value,
                changes,
                setChanges,
                reportsTo,
                setReportsTo
            );
        },
        [showWarningPopup, rowEdit, changes, reportsTo]
    );

    const handleCancelChanges = () => {
        const rolledBackReviews = originalData.map((obj) => ({ ...obj }));
        setReportsTo(rolledBackReviews);
        setDeletedItems([]);
        setChanges({});
        showMessage({
            message: t("common:changesCancelled"),
            type: "info",
            duration: 3000,
        });
    };

    // * Validator 
    // TODO Can be abstracted as well probably -- But column names matter ? 
    const validateInput = () => {
        let ret = [true, t('common:validationSuccess')];
        for (const itr of reportsTo) {
            // * Check required fields
            if (itr.managerID === '') {
                return [false, t('common:ReportsToManagerEmpty')];
            };
        };

        const notifiedUponApprovalCount = reportsTo.filter(item => item.notifieduponapprovalonly).length;
        const isPrimaryCount = reportsTo.filter(item => item.isprimary).length;
        const approvesLeavesCount = reportsTo.filter(item => item.approvesLeaves).length;
        const approvesTimesheetsCount = reportsTo.filter(item => item.approvesTimesheets).length;

        if (notifiedUponApprovalCount > 1 || isPrimaryCount > 1 || approvesLeavesCount > 1 || approvesTimesheetsCount > 1) {
            ret = [false, t('common:notValidReportsTo')]
        };

        return ret;
    };

    const handleSave = async () => {
        try {
            if (Object.entries(changes).length == 0 && deletedItems.length == 0) {
                showMessage({
                    message: t('common:noChanges'),
                    type: "danger",
                    duration: 3000,
                })
                return;
            }

            let valRet = validateInput();
            if (!valRet[0]) {
                showMessage({
                    message: valRet[1],
                    type: "danger",
                    duration: 3000,
                })
                return;
            }

            let savePromises = [];
            let updateSucc = true;

            savePromises.push(
                saveReportsTo(
                    changes,
                    props?.selectedEmployee?.employeeID
                ).then((resp) => {

                }).catch((error) => {
                    console.error("Save error", error);
                    updateSucc = false;
                })
            );
            if (deletedItems.length > 0) {
                savePromises.push(deleteReportsTo(deletedItems)
                    .then((res) => {
                        if (res) {
                            removeFromChangesAfterSave();
                        }
                    }).catch((error) => {
                        console.error("Save error", error);
                        updateSucc = false;
                    })
                );
            };
            // Wait for all promises to resolve
            await Promise.all(savePromises);
            // === Handle results ===
            if (!updateSucc) {
                showMessage({
                    message: t("common:errorSave"),
                    type: "danger",
                    duration: 3000,
                });
                return;
            }
            // * Reload after save
            loadData();
            removeFromChangesAfterSave();
            // === Success message ===
            showMessage({
                message: t("common:reportsToSuccMess"),
                type: "success",
                duration: 3000,
            });

        } catch (error) {
            console.log("Save error", error);
            showMessage({
                message: t("common:errorSave"),
                type: "danger",
                duration: 3000,
            });
        };
    };

    const handleDelete = (item, index, attribute) => {
        rowDelete(item, index, attribute, changes, setChanges, reportsTo, setReportsTo, setDeletedItems, ref)
    }

    const removeFromChangesAfterSave = () => {
        setDeletedItems([]);
        setChanges({});
    };

    const renderReportsTo = useCallback(
        ({ item, index }) => (
            <Card style={[common.cardstyle]}>
                <Card.Content>
                    <ReportsToListView
                        item={item}
                        index={index}
                        handleEditPress={handleEditPress}
                        reportsToHelper={reportsToHelper}
                        styles={styles}
                        t={t}
                        handleDelete={handleDelete}
                    />
                </Card.Content>
            </Card>
        ),
        [
            reportsTo,
            handleEditPress,
            handleDelete,
            styles,
        ]
    );

    if (isLoading) {
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        );
    }

    return (
        <SafeAreaView style={styles.mainView}>
            <View style={[styles.buttons, styles.functionalityButtons]}>
                <CustomButton
                    text={t("common:new")}
                    onPress={() => {
                        rowAdd(
                            reportsTo,
                            setReportsTo,
                            ['rowguid', 'employeeID'],
                            ['notifieduponapprovalonly', 'isprimary', 'approvesLeaves', 'approvesTimesheets'],
                            ref,
                            t,
                            ['employeeID'],
                            undefined,
                            changes,
                            setChanges,
                            undefined,
                            undefined,
                            "rowguid",
                            false,
                            undefined
                        );
                    }}
                    type="BUTTON_ADD"
                    textTop="0"
                    sz={15}
                />
                {(Object.keys(changes).length > 0 ||
                    changes.length > 0 || deletedItems.length > 0) && (
                        <CustomButton
                            text={t("common:saveFunction")}
                            onPress={handleSave}
                            type="BUTTON_SAVE"
                            textTop="0"
                            isGradient={true}
                            sz={15}
                        />
                    )}
                <CustomButton
                    text={t("common:cancel")}
                    onPress={handleCancelChanges}
                    type="BUTTON_CANCEL"
                    textTop="0"
                    sz={15}
                />
            </View>
            <SafeAreaView style={common.divider} />
            <FlatList
                data={reportsTo}
                keyExtractor={(item, index) => `${item.rowguid}-${index}`}
                renderItem={renderReportsTo} // Use the memoized function here
                refreshing={isLoading}
                ref={ref}
                ListHeaderComponent={
                    <SafeAreaView style={common.listTitle}>
                        <SafeAreaView style={[styles.row]}>
                            <CustomLabel value={t("common:manager")} type="SUBTITLE" />
                            <CustomLabel value={t("common:notifiedUponApproval")} type="SUBTITLE" />
                            <CustomLabel value={t("common:primary")} type="SUBTITLE" />
                            <CustomLabel value={t("common:approvesLeaves")} type="SUBTITLE" />
                            <CustomLabel value={t("common:approvesTimesheets")} type="SUBTITLE" />
                            <SafeAreaView style={styles.mainView} />
                        </SafeAreaView>
                    </SafeAreaView>
                }
                ListEmptyComponent={
                    isLoading ? (
                        <ActivityIndicator animating={true} color={colors.blue} />
                    ) : (
                        <CustomLabel
                            value={t("common:noDataAvailable")}
                            type="NO_FUNCTIONS"
                        />
                    )
                }
                initialNumToRender={4}
            />
        </SafeAreaView>
    );
};

const makeStyles = (colors, width) =>
    StyleSheet.create({
        mainView: {
            flex: 1,
        },
        row: {
            flex: 1,
            flexDirection: "row",
            alignItems: "center",
        },
        item: {
            flex: 1,
            justifyContent: "center",
            marginRight: 6
        },
        item2: {
            flex: 1,
            justifyContent: "center",
            marginRight: 6,
            alignItems: "center",
            gap: 18
        },
        checkbox: {
            marginBottom: 6
        },
        buttons: {
            flexDirection: "row",
            justifyContent: "flex-end",
            alignSelf: "flex-end",
            marginRight: 15,
        },
        functionalityButtons: {
            justifyContent: "flex-end",
        },
        loader: {
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "4%",
        },
    });

export default HRAdminEmplReportsTo;
